/**
 * Nœuds variable `@` dans le HTML des textes riches.
 *
 * Le nœud Tiptap porte `{ key, name, value }` et se sérialise en un `<span>`
 * autonome : le viewer public affiche la **valeur stockée**, sans appel API et
 * sans exposer le catalogue de variables (décision du 2026-08-05,
 * `docs/webviewer/roadmap-blocs.md` §1).
 *
 *   <span data-type="proposal-variable"
 *         data-variable-key="opportunity.NAME"
 *         data-variable-name="Nom de l'opportunité"
 *         data-variable-value="Ville de Test"
 *         class="proposal-variable">Ville de Test</span>
 *
 * Deux états de rendu : **résolu** (valeur non vide) → on affiche la valeur ;
 * **non résolu** → on affiche le nom, comme dans l'éditeur de template web où
 * aucun document n'est attaché.
 */

import { applyVariableLineStyle } from '../../shared/variables/variableLineStyle';

export const VARIABLE_NODE_NAME = 'proposalVariable';
export const VARIABLE_DATA_TYPE = 'proposal-variable';
/** Classe globale (pas un module CSS) : elle voyage dans le HTML persisté. */
export const VARIABLE_CLASS = 'proposal-variable';

/**
 * Texte affiché : la valeur si elle existe, sinon le nom de la variable. Une
 * adresse se rend sur le nombre de lignes choisi pour cette occurrence, comme
 * au classique (cf. shared/variables/variableLineStyle).
 */
export function variableDisplayText({
  name, value, lineStyle, separator,
}) {
  const resolved = applyVariableLineStyle(String(value ?? ''), lineStyle, separator);
  return resolved || String(name ?? '');
}

/* Même échappement que la sérialisation DOM (`editor.getHTML()`) : sinon un
   simple rafraîchissement réécrirait des nœuds identiques. */
function escapeAttribute(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');
}

function escapeText(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Attributs DOM du nœud — partagés par le rendu Tiptap et la sérialisation. */
export function variableHtmlAttributes(variable) {
  const {
    key, name, value, orphan = false, lineStyle = null, separator = null,
  } = variable;
  return {
    'data-type': VARIABLE_DATA_TYPE,
    'data-variable-key': key || '',
    'data-variable-name': name || '',
    'data-variable-value': value || '',
    ...(orphan ? { 'data-variable-orphan': 'true' } : {}),
    ...(lineStyle ? { 'data-variable-line-style': lineStyle } : {}),
    ...(separator ? { 'data-variable-separator': separator } : {}),
    // Adresse sur plusieurs lignes : les retours à la ligne de la valeur se
    // voient, partout où le HTML est rendu (éditeur et site publié).
    ...(variableDisplayText(variable).includes('\n') ? { style: 'white-space: pre-line' } : {}),
    class: VARIABLE_CLASS,
  };
}

/** Sérialisation d'un nœud variable en HTML (hors éditeur Tiptap). */
export function renderVariableHtml(variable) {
  const attrs = variableHtmlAttributes(variable);
  const serialized = Object.entries(attrs)
    .map(([attr, value]) => `${attr}="${escapeAttribute(value)}"`)
    .join(' ');
  return `<span ${serialized}>${escapeText(variableDisplayText(variable))}</span>`;
}

/** Repère un `<span>` de variable dans une chaîne HTML (nœud atomique, sans balise imbriquée). */
const VARIABLE_SPAN_RE = new RegExp(
  `<span\\b[^>]*data-type="${VARIABLE_DATA_TYPE}"[^>]*>[^<]*</span>`,
  'g',
);

export function htmlHasVariableNodes(html) {
  return typeof html === 'string' && html.includes(`data-type="${VARIABLE_DATA_TYPE}"`);
}

/** Attributs d'un `<span>` de variable isolé. */
function parseVariableSpan(spanHtml) {
  const doc = new DOMParser().parseFromString(spanHtml, 'text/html');
  const el = doc.body.firstElementChild;
  if (!el) return null;
  return {
    key: el.getAttribute('data-variable-key') || '',
    name: el.getAttribute('data-variable-name') || '',
    value: el.getAttribute('data-variable-value') || '',
    orphan: el.getAttribute('data-variable-orphan') === 'true',
    lineStyle: el.getAttribute('data-variable-line-style'),
    separator: el.getAttribute('data-variable-separator'),
  };
}

/**
 * Résout les nœuds variable d'une chaîne HTML depuis un catalogue.
 *
 * - clé connue → la `value` du catalogue est écrite, **même si le nœud était
 *   resté vide** (parité template → document) et le marqueur orphelin retiré ;
 * - clé disparue du catalogue → la **dernière valeur connue est conservée** et
 *   le nœud est marqué orphelin (signalé dans l'éditeur seulement : le site
 *   publié ne doit jamais afficher de trou au client).
 *
 * @param {string} html
 * @param {object} byKey - `catalog.byKey`
 * @returns {string} le HTML, inchangé (même référence) s'il n'y avait rien à faire
 */
export function resolveVariableNodesInHtml(html, byKey = {}, { partial = false } = {}) {
  if (!htmlHasVariableNodes(html)) return html;

  return html.replace(VARIABLE_SPAN_RE, (spanHtml) => {
    const current = parseVariableSpan(spanHtml);
    if (!current || !current.key) return spanHtml;

    const known = byKey[current.key];
    if (!known && partial) return spanHtml;
    // Le nombre de lignes et le séparateur sont ceux de l'occurrence : ils
    // survivent au rafraîchissement de la valeur.
    const next = known
      ? { ...current, name: known.name || current.name, value: known.value || '', orphan: false }
      : { ...current, orphan: true };

    const rendered = renderVariableHtml(next);
    return rendered === spanHtml ? spanHtml : rendered;
  });
}

/**
 * Objet portant le triplet variable en props — la cible d'un bouton
 * (`kind: 'variable'`) et les props du bloc « Contenu externe ».
 *
 * Ce n'est pas un nœud TipTap mais des props : le balayage de HTML ne les voit
 * pas. Elles portent pourtant le même triplet clé/nom/valeur et doivent suivre
 * exactement la même règle de rafraîchissement, sans quoi « Rafraîchir »
 * mettrait à jour le texte d'un bouton mais pas l'adresse où il mène.
 *
 * @returns {object} la **même référence** si rien ne change
 */
function resolveVariableFields(link, byKey, partial) {
  const known = byKey[link.variableKey];
  if (!known && partial) return link;
  const next = known
    ? {
      ...link,
      variableName: known.name || link.variableName,
      variableValue: known.value || '',
      variableOrphan: false,
    }
    // Clé disparue du CRM : on garde la dernière valeur connue — le site publié
    // ne doit pas perdre son lien — et on marque l'orphelin pour l'éditeur.
    : { ...link, variableOrphan: true };

  const changed = next.variableName !== link.variableName
    || next.variableValue !== link.variableValue
    || next.variableOrphan !== (link.variableOrphan === true);
  return changed ? next : link;
}

/**
 * Un objet branché sur une variable. Le `kind` n'entre pas dans le test : le
 * bloc « Contenu externe » porte le même triplet à plat dans ses props, sans
 * cible typée.
 */
function hasVariableFields(value) {
  return typeof value.variableKey === 'string' && !!value.variableKey;
}

const VARIABLE_IMAGE_SUFFIX = 'Variable';

/**
 * Prop voisine qui branche une image sur une variable du CRM : `logoUrl` →
 * `logoUrlVariable`. L'URL reste une chaîne (forme déjà en production) ; le
 * triplet clé/nom/valeur vit à côté, comme les paliers de résolution
 * (`useMediaMetaProps`).
 */
export function variableImageProp(urlProp) {
  return `${urlProp}${VARIABLE_IMAGE_SUFFIX}`;
}

/**
 * Images d'un objet (props d'un bloc) branchées sur une variable : la prop URL
 * et son triplet.
 *
 * @returns {{ urlProp: string, binding: object }[]}
 */
export function variableImageBindings(object) {
  return Object.entries(object || {})
    .filter(([prop, binding]) => prop.endsWith(VARIABLE_IMAGE_SUFFIX) && prop !== VARIABLE_IMAGE_SUFFIX
      && binding && typeof binding === 'object' && hasVariableFields(binding))
    .map(([prop, binding]) => ({ urlProp: prop.slice(0, -VARIABLE_IMAGE_SUFFIX.length), binding }));
}

/**
 * Recopie la valeur de chaque variable image dans sa prop URL, pour que le
 * rendu n'ait rien à résoudre. Une variable orpheline garde la dernière image
 * connue, comme un texte ou un bouton.
 *
 * @returns {object} la **même référence** si rien ne change
 */
function syncVariableImageProps(object) {
  let next = object;
  variableImageBindings(object).forEach(({ urlProp, binding }) => {
    if (binding.variableOrphan) return;
    const url = binding.variableValue || '';
    if (next[urlProp] === url) return;
    if (next === object) next = { ...object };
    next[urlProp] = url;
  });
  return next;
}

/**
 * Applique `resolveVariableNodesInHtml` à toutes les chaînes d'une structure
 * (web_data complet, données Puck d'une page, section de squelette), et
 * rafraîchit au passage les cibles de bouton et les images branchées sur une
 * variable.
 *
 * Retourne la **même référence** quand rien n'a changé : les appelants peuvent
 * s'en servir pour éviter un re-render ou une écriture inutile.
 */
export function resolveVariableNodesDeep(value, byKey = {}, options = {}) {
  if (typeof value === 'string') {
    return resolveVariableNodesInHtml(value, byKey, options);
  }
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const resolved = resolveVariableNodesDeep(item, byKey, options);
      if (resolved !== item) changed = true;
      return resolved;
    });
    return changed ? next : value;
  }
  if (value && typeof value === 'object') {
    let changed = false;
    const next = {};
    Object.entries(value).forEach(([prop, item]) => {
      const resolved = resolveVariableNodesDeep(item, byKey, options);
      if (resolved !== item) changed = true;
      next[prop] = resolved;
    });
    /* La descente d'abord, la résolution ensuite : un objet peut porter le
       triplet ET des textes riches (props d'un bloc), un retour anticipé
       laisserait les seconds non résolus. */
    const object = syncVariableImageProps(changed ? next : value);
    return hasVariableFields(object) ? resolveVariableFields(object, byKey, options.partial) : object;
  }
  return value;
}

/**
 * Surcharge, pour ce site, la valeur d'une variable du CRM partout où elle
 * paraît — textes, boutons, images. Les autres variables ne bougent pas : sans
 * `partial`, une clé absente du sac serait marquée orpheline. « Rafraîchir les
 * variables » remet ensuite la valeur du CRM, comme dans le classique
 * (`reloadAllApiVariables`).
 *
 * @returns {object} la **même référence** si rien ne change
 */
export function overrideVariableDeep(value, key, { name, value: overridden }) {
  return resolveVariableNodesDeep(value, { [key]: { key, name, value: overridden } }, { partial: true });
}

/**
 * Variables des textes riches d'une structure, avec leur nom et leur valeur
 * dans le site (la dernière lue l'emporte : toutes portent la même).
 *
 * @returns {Map<string, { key: string, name: string, value: string }>}
 */
export function collectTextVariablesDeep(value, acc = new Map()) {
  if (typeof value === 'string') {
    if (!htmlHasVariableNodes(value)) return acc;
    (value.match(VARIABLE_SPAN_RE) || []).forEach((span) => {
      const parsed = parseVariableSpan(span);
      if (parsed?.key) acc.set(parsed.key, { key: parsed.key, name: parsed.name, value: parsed.value });
    });
    return acc;
  }
  if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => collectTextVariablesDeep(item, acc));
  }
  return acc;
}

/** Clés des variables présentes dans une structure (diagnostic, tests). */
export function collectVariableKeysDeep(value, acc = new Set()) {
  if (value && typeof value === 'object' && !Array.isArray(value) && hasVariableFields(value)) {
    acc.add(value.variableKey);
    // Pas de retour : l'objet peut aussi porter des textes riches à variables.
  }
  if (typeof value === 'string') {
    if (htmlHasVariableNodes(value)) {
      const matches = value.match(VARIABLE_SPAN_RE) || [];
      matches.forEach((span) => {
        const parsed = parseVariableSpan(span);
        if (parsed?.key) acc.add(parsed.key);
      });
    }
    return acc;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectVariableKeysDeep(item, acc));
    return acc;
  }
  if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => collectVariableKeysDeep(item, acc));
  }
  return acc;
}
